import { getTrackingArea } from '../../../utils/getTrackingArea';
import { ClickCountTimeSpan } from '../../components/Eagle/Usage/getUsageString';
import type { Widget } from '../../types/Widget';
import { WidgetType } from '../../types/WidgetType';
import isMostPopularWidget from '../../utils/isMostPopularWidget';
import type Localiser from '../Localiser';
import { AndroidCentral } from '../sites/definitions/AndroidCentral';
import { CreativeBloq } from '../sites/definitions/CreativeBloq';
import { Decanter } from '../sites/definitions/Decanter';
import { DigitalCameraWorld } from '../sites/definitions/DigitalCameraWorld';
import { GamesRadar } from '../sites/definitions/GamesRadar';
import { GoodToKnow } from '../sites/definitions/GoodToKnow';
import { IMore } from '../sites/definitions/IMore';
import { MarieClaireUK } from '../sites/definitions/MarieClaireUK';
import { MarieClaireUS } from '../sites/definitions/MarieClaireUS';
import { MusicRadar } from '../sites/definitions/MusicRadar';
import { Shortlist } from '../sites/definitions/Shortlist';
import { T3 } from '../sites/definitions/T3';
import { TechRadar } from '../sites/definitions/TechRadar';
import { TechRadarPro } from '../sites/definitions/TechRadarPro';
import { TomsGuide } from '../sites/definitions/TomsGuide';
import { TomsHardware } from '../sites/definitions/TomsHardware';
import { WhatHiFi } from '../sites/definitions/WhatHiFi';
import { WindowsCentral } from '../sites/definitions/WindowsCentral';
import { WomanAndHome } from '../sites/definitions/WomanAndHome';
import { Site } from '../sites/Site';
import widgetMap from '../widgetConfigs';

import BaseFeatures, { ArticleType, Platform } from './BaseFeatures/BaseFeatures';
import FeatureId from './types/FeatureId';
import FeatureType from './types/FeatureType';
import FeatureValue from './types/FeatureValue';

export default class extends BaseFeatures {
  constructor(
    attributes: Record<string, string | undefined>,
    widget: Widget,
    editorial: boolean,
    localiser: Localiser,
    site: Site,
    territory?: string,
    keywords?: string[],
  ) {
    super(attributes, widget, editorial, localiser, site, territory, keywords);

    this.features = [
      {
        id: FeatureId.ARTICLE_CATEGORY,
        type: FeatureType.TEXT,
        value: FeatureValue.ARTICLE_CATEGORY,
      },
      {
        id: FeatureId.CONTRACTS_ONLY,
        type: FeatureType.BOOL,
        value: FeatureValue.CONTRACTS_ONLY,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.DEALS_ONLY,
        type: FeatureType.BOOL,
        value: FeatureValue.DEALS_ONLY,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.DISPLAY_TYPE,
        type: FeatureType.TEXT,
        value: FeatureValue.DISPLAY_TYPE,
      },
      {
        id: FeatureId.WIDGET_INTRODUCTION,
        type: FeatureType.TEXT,
        value: FeatureValue.WIDGET_INTRODUCTION,
      },
      {
        id: FeatureId.SHOW_FALLBACK,
        type: FeatureType.TEXT,
        value: FeatureValue.SHOW_FALLBACK,
      },
      {
        id: FeatureId.LINK,
        type: FeatureType.TEXT,
        value: FeatureValue.LINK,
      },
      {
        id: FeatureId.SHOW_NO_DEALS,
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_NO_DEALS,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.LINK_TEXT,
        type: FeatureType.TEXT,
        value: FeatureValue.LINK_TEXT,
      },
      {
        id: FeatureId.IMAGE,
        type: FeatureType.TEXT,
        value: FeatureValue.IMAGE,
      },
      {
        id: FeatureId.NAME,
        type: FeatureType.TEXT,
        value: FeatureValue.NAME,
      },
      {
        id: FeatureId.TRACKING_AREA,
        type: FeatureType.TEXT,
        value: FeatureValue.TRACKING_AREA,
        getDefaultValue: (): string | undefined => getTrackingArea(),
      },
      {
        id: FeatureId.SKIP,
        type: FeatureType.LIST,
        value: FeatureValue.SKIP,
        options: [
          {
            value: '',
            formatted_value: 'Always render',
          },
          {
            value: 'editorial',
            formatted_value: 'Skip if there is an Editorial widget on the page',
          },
          {
            value: 'dealsy',
            formatted_value: 'Skip if the article is black friday, prime day or a deals article',
          },
          {
            value: 'disabled',
            formatted_value:
              'Skip if the widget is disabled (determined by checking for van-hawk-none in the hawk client)',
          },
        ],
        getDefaultValue: (): null => null,
      },
      {
        id: FeatureId.ORIGIN,
        type: FeatureType.TEXT,
        value: FeatureValue.ORIGIN,
      },
      {
        id: FeatureId.STAR,
        type: FeatureType.TEXT,
        value: FeatureValue.STAR,
      },
      {
        id: FeatureId.STAR_LABEL,
        type: FeatureType.TEXT,
        value: FeatureValue.STAR_LABEL,
      },
      {
        id: FeatureId.IS_PREFERRED_PARTNER,
        type: FeatureType.BOOL,
        value: FeatureValue.IS_PREFERRED_PARTNER,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.EXCLUDE_FROM,
        type: FeatureType.TEXT,
        value: FeatureValue.EXCLUDE_FROM,
      },
      {
        id: FeatureId.WIDGET_TYPE,
        label: 'Widget Type',
        type: FeatureType.LIST,
        value: FeatureValue.WIDGET_TYPE,
        options: widgetMap.map((w) => {
          return {
            value: w.type,
            formatted_value: w.label,
          };
        }),
        getDefaultValue: (): string => {
          const { widget } = this;
          return widget.type;
        },
        battle: true,
      },
      {
        id: FeatureId.SHOW_LINK,
        label: 'All Deals Link',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_LINK,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.HIDDEN_DEAL_WIDGET,
        label: 'Hidden Deal Widget',
        type: FeatureType.BOOL,
        value: FeatureValue.HIDDEN_DEAL_WIDGET,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.SHOW_TOGGLE,
        label: 'Widget Toggle',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_TOGGLE,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SHOW_FILTERS,
        label: 'Show Filters',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_FILTERS,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.BROADBAND:
            case WidgetType.COMPARISON:
            case WidgetType.POCKET:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.SHOW_ACTIVE_FILTERS,
        label: 'Show Active Filters',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_ACTIVE_FILTERS,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.BLACKLISTED_FILTERS,
        label: 'Blacklisted Filters',
        type: FeatureType.TEXT,
        value: FeatureValue.BLACKLISTED_FILTERS,
        battle: true,
      },
      {
        id: FeatureId.SHOW_FILTER_COUNTS,
        label: 'Show Filter Counts',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_FILTER_COUNTS,
        battle: true,
        getDefaultValue: (): boolean => {
          return false;
        },
      },
      {
        id: FeatureId.GAME_PLATFORM_TABS,
        label: 'Game Platform Tabs',
        type: FeatureType.BOOL,
        value: FeatureValue.GAME_PLATFORM_TABS,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.TABS,
        label: 'Tabs',
        type: FeatureType.LIST,
        value: FeatureValue.TABS,
        battle: true,
        options: [
          {
            value: 'simOnly',
            formatted_value: 'Sim Only Tab',
          },
          {
            value: 'priority',
            formatted_value: 'Priority Label Tabs',
          },
          {
            value: 'network',
            formatted_value: 'Network/Provider Tabs',
          },
          {
            value: 'requestedModel',
            formatted_value: 'Requested Model Tabs',
          },
        ],
        getDefaultValue: (): string | null => {
          const { attributes, widget } = this;
          const defaultValue = ((): string | null => {
            switch (widget.type) {
              case WidgetType.COMPARISON:
                return 'simOnly';
              case WidgetType.SPECS:
                return 'requestedModel';
              case WidgetType.TABS:
                return 'priority';
              default:
                return null;
            }
          })();

          if (attributes.gamePlatformTabs) {
            return 'gamePlatform';
          }

          return defaultValue;
        },
      },
      {
        id: FeatureId.SHOW_BUNDLE_OFFERS,
        label: 'Show Bundle Offers',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_BUNDLE_OFFERS,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.BUNDLE:
              return true;
            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.SHOW_COUPONS,
        label: 'Coupons',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_COUPONS,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.SHOW_BRAND,
        label: 'Brand',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_BRAND,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.SHOW_MERCHANT_NAME,
        label: 'Merchant Name',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_MERCHANT_NAME,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.SHOW_FAVOURITING,
        label: 'Favouriting',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_FAVOURITING,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SHOW_FAVOURITES,
        label: 'Show Favourites',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_FAVOURITES,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SEASONAL,
        label: 'Seasonal',
        type: FeatureType.LIST,
        value: FeatureValue.SEASONAL,
        options: [
          {
            value: 'event',
            formatted_value: 'Show Seasonal Event',
          },
          {
            value: 'noEvent',
            formatted_value: "Don't Show Event",
          },
        ],
        battle: true,
        getDefaultValue: (): string => 'event',
      },
      {
        id: FeatureId.AVAILABLE_FROM,
        label: 'Available From',
        type: FeatureType.BOOL,
        value: FeatureValue.AVAILABLE_FROM,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SHOW_PRICE,
        label: 'Show Price',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_PRICE,
        options: [
          {
            value: 'showPrice',
            formatted_value: 'Show Price',
          },
          {
            value: 'noPrice',
            formatted_value: "Don't Show Price",
          },
          {
            value: 'availableFrom',
            formatted_value: "Don't show Prices and show Available from",
          },
          {
            value: 'stream',
            formatted_value: 'Show Stream on and show price',
          },
          {
            value: 'availableToStream',
            formatted_value: "Show Available to and don't show Prices",
          },
          {
            value: 'watchAt',
            formatted_value: "Show Watch At and don't show Prices",
          },
          {
            value: 'loggedInOnly',
            formatted_value: 'Display Prices to logged-in users',
          },
        ],
        battle: true,
        getDefaultValue: (): string => {
          const { defaultTab, widget, site, attributes } = this;

          if (widget.type === WidgetType.TABLETOP) {
            if (site === WhatHiFi) {
              return 'showPrice';
            }
            return 'noPrice';
          }
          // No price on Vanilla buying guides for retail price widgets
          // (except Whathifi & MarieclaireUS & Goodto)
          if (
            ['responsive', 'progressive'].includes(site.platform) &&
            ![WhatHiFi, MarieClaireUS, GoodToKnow, Decanter].includes(site) &&
            attributes.articleType === ArticleType.BUYING_GUIDE &&
            defaultTab?.category === 'retail' &&
            (widget.type === WidgetType.PRICE || widget.type === WidgetType.MERCHANT_LINK)
          ) {
            return 'noPrice';
          }
          if (defaultTab?.category === 'multimedia' && widget.type === WidgetType.MERCHANT_LINK) {
            return 'stream';
          }
          if (widget.type === WidgetType.STREAMING) {
            return 'watchAt';
          }

          return 'showPrice';
        },
      },
      {
        id: FeatureId.SHOW_WAS_PRICE,
        label: 'Was Price',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_WAS_PRICE,
        options: [
          {
            value: 'price',
            formatted_value: 'Only Show Price',
          },
          {
            value: 'promoPercentage',
            formatted_value: 'Only Show Percentage Discount Promo',
          },
          {
            value: 'allPercentage',
            formatted_value: 'Show Price & Percentage Discount Promo',
          },
          {
            value: 'promoSaving',
            formatted_value: 'Only Show Monetary Discount Promo',
          },
          {
            value: 'allSaving',
            formatted_value: 'Show Price & Monetary Discount Promo',
          },
          {
            value: 'percentagePrice',
            formatted_value: 'Show Price Percentage Under Price',
          },
          {
            value: 'promoPercentageAndSaving',
            formatted_value: 'Show Percentage and Monetary Discount Promo',
          },
        ],
        battle: true,
        getDefaultValue: (): string => {
          const { widget, site } = this;
          switch (widget.type) {
            case WidgetType.MERCHANT_LINK:
              switch (site) {
                case MarieClaireUS:
                case MarieClaireUK:
                  return 'price';
              }
              return 'noPrice';
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.REVIEW:
            case WidgetType.ANF_REVIEW:
            case WidgetType.COMPARISON:
            case WidgetType.BUNDLE:
            case WidgetType.PRODUCT_NAME:
            case WidgetType.TABS:
            case WidgetType.PARTNER:
              return 'price';
            case WidgetType.PROMOTION:
              return 'promoPercentage';
            default:
              return 'noPrice';
          }
        },
      },
      {
        id: FeatureId.SHOW_PRICE_ROUNDED,
        label: 'Round Price',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_PRICE_ROUNDED,
        battle: true,
        getDefaultValue: (): string => {
          const { site } = this;
          if ([MarieClaireUK, MarieClaireUS].includes(site)) {
            return 'yes';
          }
          return 'no';
        },
        options: [
          {
            value: '',
            formatted_value: 'Select if you want to round prices',
          },
          {
            value: 'yes',
            formatted_value: 'Show price rounded',
          },
          {
            value: 'no',
            formatted_value: 'Show price not rounded',
          },
        ],
      },
      {
        id: FeatureId.SHOW_REVIEWS,
        label: 'Star Ratings',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_REVIEWS,
        options: [
          {
            value: '',
            formatted_value: 'Select Star Ratings',
          },
          {
            value: 'all',
            formatted_value: 'Show All Star Ratings',
          },
          {
            value: 'priority',
            formatted_value: 'Show Priority Star Ratings',
          },
          {
            value: 'reversePriority',
            formatted_value: 'Show Reverse Priority Star Ratings',
          },
          {
            value: 'asin',
            formatted_value: 'Show Amazon Star Rating',
          },
          {
            value: 'walmart',
            formatted_value: 'Show Walmart Star Rating',
          },
          {
            value: 'streamingScores',
            formatted_value: 'Show IMDB and Rotten Tomatoes Sscores',
          },
          {
            value: 'none',
            formatted_value: 'Do not Show Any Star Rating',
          },
        ],
        battle: true,
        getDefaultValue: (): string => {
          const { widget, defaultTab } = this;

          switch (widget.type) {
            case WidgetType.BUNDLE:
            case WidgetType.TABLETOP:
              return 'none';
            case WidgetType.STREAMING:
            case WidgetType.MERCHANT_LINK:
              return 'streamingScores';
            default:
              if (defaultTab?.category === 'multimedia') {
                return 'streamingScores';
              }
              return 'priority';
          }
        },
      },
      {
        id: FeatureId.SHOW_REVIEWS_POSITION,
        label: 'Star Ratings Position',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_REVIEWS_POSITION,
        options: [
          {
            value: '',
            formatted_value: 'Select Star Ratings',
          },
          {
            value: 'default',
            formatted_value: 'Show Star Ratings in Default Position',
          },
          {
            value: 'top',
            formatted_value: 'Show Star Ratings at the Top',
          },
          {
            value: 'bottom',
            formatted_value: 'Show Star Ratings at the Bottom',
          },
        ],
        battle: true,
        getDefaultValue: (): string => 'default',
      },
      {
        id: FeatureId.WIDGET_TITLE,
        label: 'Widget Title',
        type: FeatureType.TEXT,
        value: FeatureValue.WIDGET_TITLE,
        battle: true,
        getDefaultValue: (): string => {
          const { attributes, widget, editorial, localiser, site } = this;

          // Try to translate the widget title for dynamic widgets, as
          // we don't have the translation in the hawk client
          if (attributes.widgetTitle) {
            return localiser.translate(attributes.widgetTitle);
          }
          const isPriceWidget = widget.type === WidgetType.PRICE;

          // Don't add a title for a price or tableprice widget
          if (
            editorial &&
            !isPriceWidget &&
            widget.type !== WidgetType.DEAL &&
            widget.type !== WidgetType.STICKY &&
            widget.type !== WidgetType.PARTNER
          ) {
            const modelNames = ((): string => {
              // Add a space after any comma without a space after it
              const models = (attributes.modelName || '').replace(/,\b/g, ', ');

              // Generate multi model title - replace final comma with 'and'
              if (models.split(',').length > 1) {
                const lastComma = models.lastIndexOf(',');
                return `${models.substring(0, lastComma)} ${localiser.translate(
                  'sentenceJoin',
                )} ${models.substring(lastComma + 1)}`;
              }
              return models;
            })();

            if (widget.type === WidgetType.MERCHANT_LINK || widget.type === WidgetType.STREAMING) {
              return '';
            }

            return localiser.translate('defaultWidgetTitle', [modelNames]);
          }
          if (
            widget.type === WidgetType.MERCHANT_LINK &&
            site.platform === Platform.RESPONSIVE &&
            attributes.articleType === ArticleType.REVIEW
          ) {
            return localiser.translate('fullPriceWidgetTitle').toUpperCase();
          }
          if (
            attributes.articleType === ArticleType.AWARDS &&
            (widget.type === WidgetType.REVIEW || widget.type === WidgetType.ANF_REVIEW)
          ) {
            return localiser.translate('fullPriceWidgetTitle');
          }
          return '';
        },
      },
      {
        id: FeatureId.SHOW_WIDGET_TITLE,
        label: 'Widget Title',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_WIDGET_TITLE,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.LOAD_MORE,
        label: 'Load More',
        type: FeatureType.BOOL,
        value: FeatureValue.LOAD_MORE,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.PRICE:
              return false;
            default:
              return true;
          }
        },
      },
      {
        id: FeatureId.SHOW_SHIPPING,
        label: 'Shipping',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_SHIPPING,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.SHOW_SHIPPING_POSITION,
        label: 'Prime Logo Position',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_SHIPPING_POSITION,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select Prime Logo Position',
          },
          {
            value: 'top',
            formatted_value: 'Show Prime Logo in Promo Flag',
          },
          {
            value: 'inside',
            formatted_value: 'Show Prime Logo inside the Button',
          },
          {
            value: 'next',
            formatted_value: 'Show Prime Logo next the Button',
          },
        ],
        getDefaultValue: (): string => {
          const { attributes } = this;

          if (
            site.platform === Platform.RESPONSIVE &&
            attributes.articleType === ArticleType.BUYING_GUIDE
          ) {
            return 'top';
          }

          return '';
        },
      },
      {
        id: FeatureId.SHOW_PROMOS,
        label: 'Promos',
        type: FeatureType.BOOL,
        value: FeatureValue.SHOW_PROMOS,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget } = this;

          return !(widget.type === WidgetType.STICKY || widget.type === WidgetType.PARTNER);
        },
      },
      {
        id: FeatureId.PROMOS_POSITION,
        label: 'Promos Position',
        type: FeatureType.LIST,
        value: FeatureValue.PROMOS_POSITION,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select Promos Position',
          },
          {
            value: 'above',
            formatted_value: 'Show Promos at the Top',
          },
          {
            value: 'below',
            formatted_value: 'Show Promos at the Bottom',
          },
          {
            value: 'hidden',
            formatted_value: "Don't show Promos",
          },
        ],
        getDefaultValue: (): string => {
          const { widget } = this;

          if (
            widget.type === WidgetType.EAGLE ||
            widget.type === WidgetType.MOST_POPULAR_OFFERS ||
            widget.type === WidgetType.TILE
          ) {
            return 'hidden';
          }

          return 'above';
        },
      },
      {
        id: FeatureId.CUSTOM_PROMO,
        type: FeatureType.TEXT,
        value: FeatureValue.CUSTOM_PROMO,
      },
      {
        id: FeatureId.CUSTOM_PROMOS,
        type: FeatureType.TEXT,
        value: FeatureValue.CUSTOM_PROMOS,
      },
      {
        id: FeatureId.COUNTDOWN_POSITION,
        label: 'Countdown Position',
        type: FeatureType.LIST,
        value: FeatureValue.COUNTDOWN_POSITION,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select Countdown Position',
          },
          {
            value: 'above',
            formatted_value: 'Show Countdown at the Top',
          },
          {
            value: 'below',
            formatted_value: 'Show Countdown at the Bottom',
          },
          {
            value: 'hidden',
            formatted_value: "Don't show Countdown",
          },
        ],
        getDefaultValue: (): string => {
          const { widget } = this;

          if (
            widget.type === WidgetType.EAGLE ||
            widget.type === WidgetType.MOST_POPULAR_OFFERS ||
            widget.type === WidgetType.TILE ||
            widget.type === WidgetType.BROADBAND
          ) {
            return 'hidden';
          }

          return 'below';
        },
      },
      {
        id: FeatureId.DISPLAY_BUNDLE_ROW,
        label: 'Display Bundle Row',
        type: FeatureType.BOOL,
        value: FeatureValue.DISPLAY_BUNDLEROW,
        battle: true,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.DISPLAY_NAME_TYPE,
        label: 'Type of Display Name',
        type: FeatureType.LIST,
        value: FeatureValue.DISPLAY_NAME_TYPE,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select Type of Display Name',
          },
          {
            value: 'fullName',
            formatted_value: 'Model name with Labels',
          },
          {
            value: 'product',
            formatted_value: 'Product name',
          },
          {
            value: 'model',
            formatted_value: 'Model name',
          },
          {
            value: 'modelWithBrand',
            formatted_value: 'Model name with Brand',
          },
          {
            value: 'contractLength',
            formatted_value: 'Contract length',
          },
          {
            value: 'brandMerchant',
            formatted_value: 'Model name with Brand or the Merchant Name',
          },
        ],
        getDefaultValue: (): string => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.COMPARISON:
            case WidgetType.MULTI_MODEL_REVIEW:
            case WidgetType.PARTNER:
              return 'modelWithBrand';
            case WidgetType.MAGAZINE_SUBSCRIPTIONS:
            case WidgetType.PEACOCK:
            case WidgetType.PRICE:
            case WidgetType.PRODUCT_NAME:
            case WidgetType.PROMOTION:
            case WidgetType.SIMPLE:
              return 'product';
            case WidgetType.POCKET:
            case WidgetType.REVIEW:
            case WidgetType.ANF_REVIEW:
            case WidgetType.BROADBAND:
              return 'fullName';
            default:
              return '';
          }
        },
      },
      {
        id: FeatureId.DISPLAY_NAME_EXPANDABLE,
        label: 'Ellipsis Display Name',
        type: FeatureType.BOOL,
        value: FeatureValue.DISPLAY_NAME_EXPANDABLE,
        battle: true,
        getDefaultValue: (): boolean => {
          const { attributes } = this;
          return isMostPopularWidget(attributes.name ?? '', site.platform);
        },
      },
      {
        id: FeatureId.DISPLAY_NAME_MAX,
        label: 'Ellipsis Display Name',
        type: FeatureType.NUMBER,
        value: FeatureValue.DISPLAY_NAME_MAX,
        battle: true,
        getDefaultValue: (): number | null => {
          const { widget } = this;

          switch (widget.type) {
            case WidgetType.BUNDLE:
              return 60;
            case WidgetType.PEACOCK:
              return 18;
            case WidgetType.TILE:
            case WidgetType.EAGLE:
              return 250;
            default:
              return null;
          }
        },
      },
      {
        id: FeatureId.SERVERSIDE_IMAGES,
        type: FeatureType.BOOL,
        value: FeatureValue.SERVERSIDE_IMAGES,
        getDefaultValue: (): boolean => true,
      },
      {
        id: FeatureId.NAVIGATION,
        type: FeatureType.TEXT,
        value: FeatureValue.NAVIGATION,
      },
      {
        id: FeatureId.RELATED_PAGES,
        type: FeatureType.TEXT,
        value: FeatureValue.RELATED_PAGES,
      },
      {
        id: FeatureId.PARENT_PAGE,
        type: FeatureType.TEXT,
        value: FeatureValue.PARENT_PAGE,
      },
      {
        id: FeatureId.BREADCRUMBS,
        type: FeatureType.TEXT,
        value: FeatureValue.BREADCRUMBS,
      },
      {
        id: FeatureId.BUTTON_TYPE,
        label: 'Button Type',
        type: FeatureType.LIST,
        value: FeatureValue.BUTTON_TYPE,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select Button Type',
          },
          {
            value: 'chevron',
            formatted_value: 'Chevron button',
          },
          {
            value: 'standard',
            formatted_value: 'Standard button',
          },
          {
            value: 'textbased',
            formatted_value: 'Merchant link text-based variant',
          },
        ],
        getDefaultValue: (): string => {
          const { widget, site } = this;

          switch (widget.type) {
            case WidgetType.BUNDLE:
              return 'chevron';
            case WidgetType.MERCHANT_LINK:
              switch (site) {
                case AndroidCentral:
                case IMore:
                case WindowsCentral:
                case MarieClaireUK:
                case MarieClaireUS:
                case WomanAndHome:
                case Shortlist:
                case Decanter:
                  return '';
                default:
                  return 'textbased';
              }
            default:
              return '';
          }
        },
      },
      {
        id: FeatureId.ALL_DEALS_LINK,
        label: 'All deals link',
        type: FeatureType.TEXT,
        value: FeatureValue.ALL_DEALS_LINK,
      },
      {
        id: FeatureId.ALL_DEALS_LINK_TEXT,
        label: 'All deals link text',
        type: FeatureType.TEXT,
        value: FeatureValue.ALL_DEALS_LINK_TEXT,
      },
      {
        id: FeatureId.VISIBLE_MATCH_ID,
        label: 'Visible Match Id',
        type: FeatureType.TEXT,
        value: FeatureValue.VISIBLE_MATCH_ID,
      },
      {
        id: FeatureId.SEEN_MATCH_ID,
        label: 'Seen Match Id',
        type: FeatureType.TEXT,
        value: FeatureValue.SEEN_MATCH_ID,
      },
      {
        id: FeatureId.PRESERVE_EDITORIAL_LINK,
        label: 'Preserve Editorial Link',
        type: FeatureType.BOOL,
        value: FeatureValue.PRESERVE_EDITORIAL_LINK,
        battle: true,
        getDefaultValue: (): boolean => {
          const { widget, site, territory, attributes } = this;

          if (
            widget.type === WidgetType.PRODUCT_BLOCK ||
            (widget.type === WidgetType.MERCHANT_LINK && attributes.blockType === 'buying-guide')
          ) {
            return true;
          }

          switch (site) {
            case MarieClaireUS:
            case WomanAndHome:
            case Decanter:
              return widget.type === WidgetType.MERCHANT_LINK && territory === 'US';

            default:
              return false;
          }
        },
      },
      {
        id: FeatureId.USEHAWK_PRODUCT_IMAGES,
        label: 'Use Hawk product images',
        type: FeatureType.BOOL,
        value: FeatureValue.USE_HAWK_PRODUCT_IMAGES,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SHOW_DEAL_OFFER_LABELS,
        label: 'Show Deal Offer Labels',
        type: FeatureType.LIST,
        value: FeatureValue.SHOW_DEAL_OFFER_LABELS,
        battle: true,
        options: [
          {
            value: '',
            formatted_value: 'Select which deal labels to display',
          },
          {
            value: 'display_labels',
            formatted_value: 'Display Labels',
          },
          {
            value: 'display_primary_label',
            formatted_value: 'Display Primary Label',
          },
        ],
        getDefaultValue: (): string => {
          const { site, widget } = this;
          if (
            widget.type === WidgetType.MERCHANT_LINK &&
            [
              T3,
              TechRadar,
              TechRadarPro,
              TomsGuide,
              GamesRadar,
              AndroidCentral,
              MusicRadar,
              TomsHardware,
              WhatHiFi,
              CreativeBloq,
              DigitalCameraWorld,
            ].includes(site)
          ) {
            return 'display_labels';
          }
          return '';
        },
      },
      {
        id: FeatureId.AVAILABLE_FROM_CLICKABLE,
        label: 'Available From Clickable',
        type: FeatureType.BOOL,
        value: FeatureValue.AVAILABLE_FROM_CLICKABLE,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.EDITORIAL_WAS_PRICE,
        type: FeatureType.TEXT,
        value: FeatureValue.EDITORIAL_WAS_PRICE,
      },
      {
        id: FeatureId.EDITORIAL_PRICE,
        type: FeatureType.TEXT,
        value: FeatureValue.EDITORIAL_PRICE,
      },
      {
        id: FeatureId.EDITORIAL_CURRENCY,
        type: FeatureType.TEXT,
        value: FeatureValue.EDITORIAL_CURRENCY,
      },
      {
        id: FeatureId.MERCHANTLINK_TEXT_TYPE,
        label: 'Merchantlink Text type',
        type: FeatureType.LIST,
        value: FeatureValue.MERCHANTLINK_TEXT_TYPE,
        options: [
          {
            value: 'defaultMerchantLinkText',
            formatted_value: 'View at *',
          },
          {
            value: 'buyAtMerchantLinkText',
            formatted_value: 'Buy at *   ',
          },
          {
            value: 'shopAtMerchantLinkText',
            formatted_value: 'Shop at *',
          },
        ],
        battle: true,
        getDefaultValue: (): null => null,
      },
      {
        id: FeatureId.DEAL_WIDGET_COLLAPSIBLE_TEXT,
        label: 'Deal Widget Collapsible Text',
        type: FeatureType.BOOL,
        value: FeatureValue.DEAL_WIDGET_COLLAPSIBLE_TEXT,
        battle: true,
        getDefaultValue: (): boolean => {
          return false;
        },
      },
      {
        id: FeatureId.MODEL_BRAND,
        type: FeatureType.TEXT,
        value: FeatureValue.MODEL_BRAND,
      },
      {
        id: FeatureId.MODEL_PRODUCT_NAME,
        type: FeatureType.TEXT,
        value: FeatureValue.MODEL_PRODUCT_NAME,
      },
      {
        id: FeatureId.VERIFIED,
        label: 'Verified status visibility',
        type: FeatureType.LIST,
        value: FeatureValue.VERIFIED,
        options: [
          {
            value: 'exclusive',
            formatted_value: 'Verified status is present on exclusive offers ',
          },
          {
            value: 'codes',
            formatted_value: 'Verified status is present on code type offers',
          },
          {
            value: 'all',
            formatted_value: 'All offers can display verified status if applicable',
          },
        ],
        getDefaultValue: (): null => null,
        battle: true,
      },
      {
        id: FeatureId.BLOCK_TYPE,
        type: FeatureType.TEXT,
        value: FeatureValue.BLOCK_TYPE,
      },
      {
        id: FeatureId.BLOCK_LAYOUT,
        type: FeatureType.TEXT,
        value: FeatureValue.BLOCK_LAYOUT,
      },
      {
        id: FeatureId.CLICK_COUNT_TIME_SPAN,
        label: 'Click count time span',
        type: FeatureType.LIST,
        value: FeatureValue.CLICK_COUNT_TIME_SPAN,
        options: [
          {
            value: ClickCountTimeSpan.NONE,
            formatted_value: 'Never display click count',
          },
          {
            value: ClickCountTimeSpan.DAILY,
            formatted_value: 'Always display daily click count',
          },
          {
            value: ClickCountTimeSpan.WEEKLY,
            formatted_value: 'Always display weekly click count',
          },
          {
            value: ClickCountTimeSpan.MONTHLY,
            formatted_value: 'Always display monthly click count',
          },
          {
            value: ClickCountTimeSpan.DYNAMIC,
            formatted_value: 'Automatically determine the most impactful time span',
          },
        ],
        battle: true,
        getDefaultValue: (): ClickCountTimeSpan => ClickCountTimeSpan.DYNAMIC,
      },
      {
        id: FeatureId.EDITORIAL_MERCHANT_NAME,
        type: FeatureType.TEXT,
        value: FeatureValue.EDITORIAL_MERCHANT_NAME,
      },
      {
        id: FeatureId.HAWK_JSON,
        type: FeatureType.JSON,
        value: FeatureValue.HAWK_JSON,
      },
      {
        id: FeatureId.IMAGE_CAPTION_TEXT,
        type: FeatureType.TEXT,
        value: FeatureValue.IMAGE_CAPTION_TEXT,
      },
      {
        id: FeatureId.COLLECTION_CATEGORY,
        type: FeatureType.TEXT,
        value: FeatureValue.COLLECTION_CATEGORY,
      },
      {
        id: FeatureId.COLLECTION_ROWS,
        type: FeatureType.NUMBER,
        value: FeatureValue.COLLECTION_ROWS,
      },
      {
        id: FeatureId.SHOW_PRODUCT_FINDER_LINK,
        type: FeatureType.BOOL,
        label: 'Show Product Finder Link',
        value: FeatureValue.SHOW_PRODUCT_FINDER_LINK,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.USE_ARS,
        type: FeatureType.BOOL,
        label: 'PPC ARS parameter',
        value: FeatureValue.USE_ARS,
        battle: true,
        getDefaultValue: (): boolean => false,
      },
      {
        id: FeatureId.SOCIAL,
        type: FeatureType.TEXT,
        value: FeatureValue.SOCIAL,
      },
    ];
  }
}
